import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  fetchSpecialtiesList,
  fetchSpecialtyDetails,
  editSpecialtyDetails,
  updateSpecialityDetails,
  trashSpecialityDetails,
  addSpecialtyToInternationalPage,
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";

const InterantionalSpecialty = () => {
  const [allSpecialties, setAllSpecialties] = useState([]);
  const [internationalSpecialties, setInternationalSpecialties] = useState([]);
  const [filteredSpecialties, setFilteredSpecialties] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, homepageRes] = await Promise.all([
        fetchSpecialtiesList(),
        fetchSpecialtyDetails(),
      ]);
      const all = (allRes.specialities || []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        img_path: item.img_path,
      }));

      setAllSpecialties(all);
      setFilteredSpecialties(allRes.data || []);
      setInternationalSpecialties(homepageRes.data || []);
    } catch {
      toast.error("Failed to fetch specialties.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let data = [...allSpecialties];

    if (search.trim()) {
      data = data.filter((s) =>
        s.name.toLowerCase().includes(search.trim().toLowerCase())
      );
    }

    if (sortField === "name") {
      data.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortField === "id") {
      data.sort((a, b) => a.id - b.id);
    }

    setFilteredSpecialties(data);
  }, [search, sortField, allSpecialties]);

  //   const handleAddSpecialty = async (specialty_id) => {
  //     if (internationalSpecialties.length >= 4) {
  //       toast.warning("Only 4 specialties can be added.");
  //       return;
  //     }

  //     const display_order = internationalSpecialties.length + 1;
  //     // const formData = { specialty_id, display_order };
  //     const formData = new FormData();
  //     formData.append("specialty_id", specialty_id);
  //     // formData.append("display_order", display_order);

  //     setLoadingAction(true);
  //     try {
  //       await addSpecialtyToInternationalPage(formData);
  //       toast.success("Specialty added to International.");
  //       fetchData();
  //     } catch {
  //       toast.error("Failed to add specialty.");
  //     } finally {
  //       setLoadingAction(false);
  //     }
  //   };

  const handleAddSpecialty = async (specialty_id) => {
    const existingOrders = internationalSpecialties.map((item) =>
      Number(item.display_order)
    );
    let display_order = 1;
    while (existingOrders.includes(display_order) && display_order <= 4) {
      display_order++;
    }

    if (display_order > 4) {
      toast.warning("Only 4 specialties can be added to International.");
      return;
    }

    // const formData = { specialty_id, display_order };
    const formData = new FormData();
    formData.append("specialty_id", specialty_id);
    formData.append("display_order", display_order);

    setLoadingAction(true);
    try {
      await addSpecialtyToInternationalPage(formData);
      toast.success("Specialty added to International.");
      fetchData();
    } catch {
      toast.error("Failed to add specialty.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await editSpecialtyDetails(id);
      setEditItem(res.data);
    } catch {
      toast.error("Failed to load specialty for editing.");
    }
  };

  const handleUpdate = async () => {
    setLoadingAction(true);
    try {
      await updateSpecialityDetails(editItem.homepage_entry_id, {
        specialty_id: editItem.specialty_id,
        display_order: editItem.display_order,
      });
      toast.success("Updated successfully.");
      setEditItem(null);
      fetchData();
    } catch {
      toast.error("Failed to update.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleTrash = async (id) => {
    try {
      await trashSpecialityDetails(id);
      toast.success("Specialty removed.");
      fetchData();
    } catch {
      toast.error("Failed to remove specialty.");
    }
  };

  const homepageSpecialtyIds = internationalSpecialties.map(
    (s) => s.specialty_id
  );

  const allColumns = [
    { field: "id", headerName: "Specialty ID", width: 120 },
    { field: "name", headerName: "Specialty Name", flex: 1 },
    {
      field: "img_path",
      headerName: "Icon",
      width: 100,
      renderCell: (params) =>
        params.row.img_path ? (
          <img
            src={`${baseImage_path}/${params.row.img_path}`}
            alt="icon"
            width={40}
            height={40}
            style={{ objectFit: "contain" }}
          />
        ) : (
          "No Icon"
        ),
    },
    {
      field: "actions",
      headerName: "Add to International",
      width: 180,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          disabled={
            homepageSpecialtyIds.includes(params.row.id) ||
            internationalSpecialties.length >= 8
          }
          onClick={() => handleAddSpecialty(params.row.id)}
        >
          Add
        </Button>
      ),
    },
  ];

  const homepageColumns = [
    { field: "homepage_entry_id", headerName: "ID", width: 80 },
    { field: "specialty_name", headerName: "Name", flex: 1 },
    { field: "display_order", headerName: "Order", width: 100 },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit(params.row.homepage_entry_id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            sx={{ ml: 1 }}
            onClick={() => handleTrash(params.row.homepage_entry_id)}
          >
            Trash
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m={2}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        International Specialties Management
      </Typography>

      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Search Specialty"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortField}
            label="Sort By"
            onChange={(e) => setSortField(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="id">ID</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        All Specialties
      </Typography>
      <DataGrid
        rows={filteredSpecialties}
        columns={allColumns}
        getRowId={(row) => row.id}
        autoHeight
        loading={loading}
      />

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        Selected International Specialties (Max 4)
      </Typography>
      <DataGrid
        rows={internationalSpecialties}
        columns={homepageColumns}
        getRowId={(row) => row.homepage_entry_id}
        autoHeight
        loading={loading}
      />

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)}>
          <DialogTitle>Edit International Specialty</DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              fullWidth
              label="Specialty ID"
              value={editItem.specialty_id}
              onChange={(e) =>
                setEditItem({ ...editItem, specialty_id: e.target.value })
              }
            />
            <TextField
              margin="normal"
              fullWidth
              label="Display Order"
              value={editItem.display_order}
              onChange={(e) =>
                setEditItem({ ...editItem, display_order: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditItem(null)}
              style={{ fontWeight: "bold", backgroundColor: "grey" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InterantionalSpecialty;
